import dagre from 'dagre';
import { Position } from 'reactflow';
import set from 'lodash/set';

const PATH_DEFAULT_NODE_POSITION = { x: 0, y: 0 };
const PATH_DEFAULT_NODE_TYPE = 'hop';
const PATH_UNIDENTIFIABLE_NODE_TYPE = 'unidentifiableHop';

/**
 * Generates path elements based on path analysis API response data
 * @param {{ agent: Object, path: Object[] }} pathResponseData path analysis response data
 * @param {any} testData data about the test the path is being generated for
 * @returns {[nodes, edges]} nodes and edges generated based on path analysis data
 */
export function generatePathElements(pathResponseData, testData) {
    const hopNodes = generateHopNodes(pathResponseData, testData);
    const hopLinks = generateHopLinks(hopNodes);

    return [hopNodes, hopLinks];
}

/**
 * Performs chart layout calculations and returns Reactflow nodes with correct positioning
 * @param {Node[]} nodes Array of Reactflow nodes to layout.
 * @param {Edge[]} edges Array of Reactflow edges for chart. Required to calculate correct node positions.
 * @returns {Node[]} the laid out Reactflow chart nodes
 */
export function layoutPathElements(nodes, edges) {
    const dagreGraph = new dagre.graphlib.Graph();

    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
        ranker: 'tight-tree',
        rankdir: 'LR',
        marginx: 32,
        nodesep: 150,
        ranksep: 50,
    });

    const nodeWidth = 160;
    const nodeHeight = 80;
    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    return nodes.map((node) => {
        const positionedNode = dagreGraph.node(node.id);

        node.targetPosition = Position.Left;
        node.sourcePosition = Position.Right;

        node.position = {
            x: positionedNode.x - nodeWidth / 2,
            y: positionedNode.y - nodeHeight / 2,
        };

        return node;
    });
}

/**
 * Generates array of path hop nodes for Reactflow visualization
 * @param {} pathResponseData path analysis response data
 * @param {any} testData data about the test the path is being generated for
 * @returns array of generated Reactflow nodes
 */
function generateHopNodes(pathResponseData, testData) {
    // TODO: Remove summary after migration of path details endpoint to msp query
    const { path: pathData, agent: agentData } = pathResponseData;
    let hopNodes = [generateAgentHopNode(pathData[0], agentData)];
    const nodeMap = new Map(['response1', 'response2', 'response3'].map((hopResponse) => [hopResponse, hopNodes]));

    // Tracking IP addresses to detect if a router is found multiple time in the path
    const ipAddressSet = new Set();
    const duplicateIpAddresses = new Set();

    // Tracking ASN/hopNumber combinations to set ISP hop labels on nodes later
    const asnMap = new Map();

    pathData.slice(1).forEach((hopData) => {
        const hopAsn = hopData.asn || hopData.location?.asn;
        const hopIpAddress = hopData.ipAddress;
        const hopResponses = getHopResponses(hopData);

        let hopNode;
        if (!hopData.ipAddress) {
            hopNode = generateUnidentifiableHopNode(hopData);
        } else {
            if (hopAsn && !asnMap.has(hopAsn)) {
                // If the hop's ASN hasn't been encountered before, this is a network switch, add it to our map
                asnMap.set(hopAsn, hopData.hopNumber);
            }

            if (ipAddressSet.has(hopIpAddress)) duplicateIpAddresses.add(hopIpAddress);

            hopNode =
                pathData.at(-1).hopNumber === hopData.hopNumber
                    ? generateTargetHopNode(hopData, testData)
                    : generateKnownHopNode(hopData);
        }
        hopNode.data.sources = generateHopSources(hopResponses, nodeMap);

        hopResponses.forEach((hopResponse) => {
            nodeMap.set(hopResponse, [...(nodeMap.get(hopResponse) || []), hopNode]);
        });

        ipAddressSet.add(hopData.ipAddress);
        hopNodes.push(hopNode);
    });

    // Update nodes that are determined to be duplicates to have their isDuplicate flag set to true
    Array.from(duplicateIpAddresses).forEach((duplicateIp) => {
        hopNodes
            .filter((hopNode) => duplicateIp === hopNode.data.hopData.ipAddress)
            .forEach((dupHopNode) => (dupHopNode.data.isDuplicate = true));
    });

    // Update nodes that are determined to be network switches using the asn map
    Array.from(asnMap.entries()).forEach(([asn, hopNumber]) => {
        hopNodes
            .filter((hopNode) => hopNumber === hopNode.data.hopData.hopNumber)
            .filter((hopNode) => asn === hopNode.data.hopData?.location?.asn)
            .forEach((hopNode) => {
                if (!hopNode.data.isTarget) {
                    hopNode.data.isIspHop = true;
                }
            });
    });

    hopNodes = condenseConsecutiveUnidentifiableHops(hopNodes, pathData);

    return hopNodes.filter((hopNode) => !hopNode.hidden);
}

/**
 * Generates Reactflow edges for hop links for path visualization
 * @param {Node[]} hopNodes array of Reactflow nodes. Note that nodes must generate their sources before we can generate the edges
 * @returns {Edge[]} array of Reactflow edges
 */
function generateHopLinks(hopNodes) {
    const hopLinks = [];

    hopNodes.forEach((hopNode) => {
        hopNode.data.sources.forEach((hopSource) => {
            hopLinks.push({
                id: `${hopSource.id}-${hopNode.id}`,
                source: hopSource.id,
                target: hopNode.id,
            });
        });
    });

    return hopLinks;
}

/**
 * Generates the root agent hop Reactflow node
 * @param {Object} agentHopData API hop data for the agent
 * @param {Object} agentData API data for the test agent
 * @returns {Node} Reactflow node for the agent
 */
function generateAgentHopNode(agentHopData, agentData) {
    return {
        id: `${agentHopData.ipAddress}-agent`,
        position: PATH_DEFAULT_NODE_POSITION,
        type: PATH_DEFAULT_NODE_TYPE,
        data: {
            sources: [],
            isAgent: true,
            hopData: agentHopData,
            agentData,
        },
    };
}

/**
 * Generates the target hop Reactflow node
 * @param {Object} targetHopData API hop data for the target node
 * @param {any} testData data about the test the path is being generated for
 * @returns {Node} Reactflow node for the target
 */
function generateTargetHopNode(targetHopData, testData) {
    return {
        id: `${targetHopData.ipAddress}-target`,
        position: PATH_DEFAULT_NODE_POSITION,
        type: PATH_DEFAULT_NODE_TYPE,
        data: {
            sources: [],
            isTarget: true,
            hopData: targetHopData,
            testData,
        },
    };
}

/**
 * Generates a known hop Reactflow node
 * @param {Object} hopData API data for a hop
 * @param {boolean} isIspHop flag informing method if the hop is an ISP hop
 * @returns {Node} Reactflow node for the passed hop data
 */
function generateKnownHopNode(hopData, isIspHop) {
    return {
        id: `${hopData.ipAddress}-${hopData.hopNumber}`,
        position: PATH_DEFAULT_NODE_POSITION,
        type: PATH_DEFAULT_NODE_TYPE,
        data: {
            sources: [],
            hopData,
            isIspHop,
        },
    };
}

/**
 * Generate and unidentifiable hop Reactflow node
 * @param {Object} hopData API data for a hop
 * @returns Reactflow node for the unidentifiable hop
 */
function generateUnidentifiableHopNode(hopData) {
    return {
        id: `unidentifiable-${hopData.hopNumber}`,
        position: PATH_DEFAULT_NODE_POSITION,
        type: 'unidentifiableHop',
        data: {
            sources: [],
            hopData,
        },
    };
}

/**
 * Generates hop sources based on node map and traceroute responses for a node
 * @param {string[]} currentHopResponses Array of the traceroute responses for the current hop. i.e. response1, response2
 * @param {Map<string, Node>} nodeMap JS Map containing mapping of traceroute responses to hop Nodes
 * @returns Array of hop sources to be used to set the sources array on a Node's data
 */
function generateHopSources(currentHopResponses, nodeMap) {
    const hopSources = new Set();

    currentHopResponses.forEach((hopResponse) => {
        const previousHopResponse = nodeMap.get(hopResponse)?.at(-1);

        if (previousHopResponse) {
            hopSources.add(previousHopResponse);
        }
    });

    return Array.from(hopSources);
}

/**
 * Gets traceroute response keys from hop data
 * @param {Object} hopData API data for a hop
 * @returns {string[]} array of traceroute response keys. i.e. response1, response2
 */
function getHopResponses(hopData) {
    const responseRegex = new RegExp('response[0-9]+');

    return Object.keys(hopData).filter((key) => responseRegex.test(key));
}

/**
 * Cosolidates groups of consecutive unidentifiable hops. There are many cases where there will be many
 * consecutive hop numbers that are all unidentifiable. This method groups those cases in to a single hop node
 * so that we can simplfiy and condense the display presented to users. This method greatly reduces the chart
 * size in many scenarios where we have more complicated path visualizations.
 * @param {Object[]} hopNodes array of generated hop nodes to perform consolidation on
 * @returns {Object[]]} array of nodes with consecutive unidentifiable hops consolidated
 */
function condenseConsecutiveUnidentifiableHops(hopNodes, pathData) {
    for (let i = 0; i < hopNodes.length; i++) {
        const hopNode = hopNodes.at(i);

        if (
            hopNode.type === PATH_UNIDENTIFIABLE_NODE_TYPE &&
            getHopResponses(hopNode.data.hopData).length === 3 &&
            hopNodes.at(i + 1)?.type === PATH_UNIDENTIFIABLE_NODE_TYPE
        ) {
            let nextHopIndex = i + 1;

            // We've detected a unidentifiable hop group, so set the hidden property on all nodes
            // in the group after the groups first node
            while (
                hopNodes.at(nextHopIndex)?.type === PATH_UNIDENTIFIABLE_NODE_TYPE &&
                getHopResponses(hopNodes.at(nextHopIndex)?.data?.hopData).length === 3 &&
                pathData.at(-1).hopNumber !== hopNodes.at(nextHopIndex)?.data?.hopData?.hopNumber
            ) {
                hopNodes.at(nextHopIndex).hidden = true;

                nextHopIndex += 1;
            }

            // Update hopNode with the ststatusMessageart and end hop numbers to display in the visualization
            const startHop = hopNode?.data?.hopData?.hopNumber;
            const endHop = hopNodes.at(nextHopIndex - 1)?.data?.hopData?.hopNumber;
            if (endHop - startHop > 0) {
                hopNode.data.isUnidentifiableGroup = true;
                set(hopNode, 'data.hopData.startHop', startHop);
                set(hopNode, 'data.hopData.endHop', endHop);
            }

            // Update the sources of the nodes in the next hop after the group to be the start node of the
            // group. This ensures that links are processed correctly
            const nextHopNumber = hopNodes.at(nextHopIndex)?.data?.hopData?.hopNumber;
            while (hopNodes.at(nextHopIndex) && hopNodes.at(nextHopIndex).data?.hopData?.hopNumber === nextHopNumber) {
                hopNodes.at(nextHopIndex).data.sources = [hopNode];

                nextHopIndex += 1;
            }

            // Don't process any nodes twice. That would be silly
            i = nextHopIndex;
        }
    }

    return hopNodes.filter((hopNode) => !hopNode.hidden);
}
